import { mergeAttributes, Node } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";
import { ImageBlockItem, type ImageRenderer } from "./image-block-item";

/**
 * The block node an uploaded picture occupies in a document. An atom like the video block: one
 * unit the caret steps over, carrying only the id of the `files` row the renderer resolves. The
 * bytes live in the organisation's bucket and are reached through a signed URL, never inlined.
 *
 * The node must sit in the shared extension list of `useTiptap` even where no renderer is
 * passed — `generateHTML` throws on any node type its extension list does not know, and the
 * catch around it falls back to showing the raw JSON string. One list serves every call site,
 * so the node ships everywhere and only the renderer is optional.
 *
 * @param imageRenderer - Resolves a file id to the picture; absent, the node view renders a
 * labelled placeholder.
 * @returns The configured extension for the editor's extension list.
 */
export function buildImageBlock(imageRenderer?: ImageRenderer) {
	return Node.create({
		name: "imageBlock",
		group: "block",
		atom: true,
		draggable: true,

		addAttributes() {
			return {
				fileId: { default: null },
			};
		},

		parseHTML() {
			return [
				{
					tag: "image-block[data-file-id]",
					getAttrs: (element) => ({
						fileId: element.getAttribute("data-file-id"),
					}),
				},
			];
		},

		renderHTML({ node, HTMLAttributes }) {
			return [
				"image-block",
				mergeAttributes(
					{
						"data-file-id": node.attrs.fileId,
					},
					HTMLAttributes,
				),
			];
		},

		addNodeView() {
			return ReactNodeViewRenderer((props) => (
				<ImageBlockItem
					{...props}
					imageRenderer={imageRenderer}
				/>
			));
		},
	});
}
